import { create } from 'zustand';

import { getAdmissionSettings, updateAdmissionSettings } from '../services/api/admissionSettingsApi';
import { getHipSettings, updateHipSettings, verifyHipSettings } from '../services/api/hipSettingsApi';
import { getHiuSettings, updateHiuSettings } from '../services/api/hiuSettingsApi';
import { getLabSettings, updateLabSettings } from '../services/api/labSettingsApi';
import {
  getProcedurePaymentSettings,
  updateProcedurePaymentSettings,
} from '../services/api/procedurePaymentSettingsApi';
import useAbdmModeStore from './abdmModeStore';
import { getConsentCatalog } from '../services/api/consentApi';

// The M2 header reads the same HIP fields from abdmModeStore: after a HIP save / re-check, refresh it
// once (single-flight refetch) - only if it was already loaded, else the page's own mount fetch covers it.
const refreshAbdmHeader = () => { if (useAbdmModeStore.getState().ready) useAbdmModeStore.getState().refetch(); };

/**
 * Org Settings store (Zustand) — lightweight, app-wide cache of the
 * clinic's procedure-payment policy flags so the procedure card, the
 * add-procedure widgets, and the catalog form can gate their UI without
 * each re-fetching. Fetched once (lazily) per session. The two flags are
 * read-only here — they're set via the backend admin script
 * (scripts/admin/payment_policy.py), not the app UI.
 *
 * Defaults mirror the backend: both flags OFF (doctors cannot collect;
 * every procedure routes through reception) until an admin changes them.
 */
const useOrgSettingsStore = create((set, get) => ({
  skipPaymentAll: false,
  doctorCanCollectPayment: false,
  // W4 / C3 — auto-complete-on-payment flag. Admin-writable (see
  // setAutoCompleteProcedures below); absent in the stored row ⇒ False.
  autoCompleteProcedures: false,
  loaded: false,
  isLoading: false,
  error: null,

  /** Fetch once unless `force` is set. Safe to call from many components. */
  fetchProcedurePaymentSettings: async ({ force = false } = {}) => {
    const { loaded, isLoading } = get();
    if ((loaded || isLoading) && !force) return;
    set({ isLoading: true, error: null });
    try {
      const data = await getProcedurePaymentSettings();
      set({
        skipPaymentAll: !!data.skip_payment_all,
        doctorCanCollectPayment: !!data.doctor_can_collect_payment,
        autoCompleteProcedures: !!data.auto_complete_procedures,
        loaded: true,
        isLoading: false,
      });
    } catch (err) {
      // Fail safe: flags stay at their default-off values and `loaded` stays
      // false so a settings-panel caller (e.g. ProcedurePaymentSettings) can
      // detect the failed load and render an "unavailable" state instead of
      // crashing — never throw into the caller.
      set({ isLoading: false, error: err?.message || 'Failed to load settings' });
    }
  },

  /** Admin — persist auto_complete_procedures and update the cached value. */
  setAutoCompleteProcedures: async (enabled) => {
    const data = await updateProcedurePaymentSettings({ auto_complete_procedures: !!enabled });
    set({ autoCompleteProcedures: !!data.auto_complete_procedures, loaded: true });
    return !!data.auto_complete_procedures;
  },

  // ── Lab module toggle (enable_lab_module) ──────────────────────────
  // Single source of truth for "is the Lab module on". Defaults to OFF so
  // the app behaves exactly as today until an admin enables it.
  enableLabModule: false,
  labLoaded: false,
  labLoading: false,

  /** Fetch the per-org lab flag once unless `force` is set. */
  fetchLabSettings: async ({ force = false } = {}) => {
    const { labLoaded, labLoading } = get();
    if ((labLoaded || labLoading) && !force) return;
    set({ labLoading: true });
    try {
      const data = await getLabSettings();
      set({ enableLabModule: !!data.enable_lab_module, labLoaded: true, labLoading: false });
    } catch (err) {
      // Transient failure (401 refresh blip, network hiccup): PRESERVE the
      // last-known flag — never latch an admin-enabled module to false on a
      // mere blip — and leave labLoaded false so the next mount
      // (useLabEnabled) self-heals by re-fetching, no re-login required.
      set({ labLoading: false, labLoaded: false });
    }
  },

  /** Admin toggle — persist the flag and update the cached value. */
  setLabModule: async (enabled) => {
    const data = await updateLabSettings(!!enabled);
    set({ enableLabModule: !!data.enable_lab_module, labLoaded: true });
    return !!data.enable_lab_module;
  },

  // ── Admission (IPD) module toggle (enable_admission_module) ────────
  // Single source of truth for "is the Admission module on". Defaults to
  // OFF so the app behaves exactly as today until an admin enables it.
  enableAdmissionModule: false,
  // DEPOSIT_LOW alarm headroom (C4 / C-8), integer paise. Default 0 = alarm the
  // moment the bill outruns the deposit. Single org knob, cached like the flag.
  depositLowThresholdPaise: 0,
  // Automatic nightly bed-day charging (bed-charging switch-off). Default OFF
  // so bed charges are added manually until an admin re-enables auto-accrual.
  bedDayAccrualEnabled: false,
  admissionLoaded: false,
  admissionLoading: false,

  /** Fetch the per-org admission flag once unless `force` is set. */
  fetchAdmissionSettings: async ({ force = false } = {}) => {
    const { admissionLoaded, admissionLoading } = get();
    if ((admissionLoaded || admissionLoading) && !force) return;
    set({ admissionLoading: true });
    try {
      const data = await getAdmissionSettings();
      set({
        enableAdmissionModule: !!data.enable_admission_module,
        depositLowThresholdPaise: Math.max(0, Math.round(Number(data.deposit_low_threshold_paise) || 0)),
        bedDayAccrualEnabled: !!data.bed_day_accrual_enabled,
        admissionLoaded: true,
        admissionLoading: false,
      });
    } catch (err) {
      // Transient failure (401 refresh blip, network hiccup): PRESERVE the
      // last-known flag — never latch an admin-enabled module to false on a
      // mere blip — and leave admissionLoaded false so the next mount
      // (useAdmissionEnabled) self-heals by re-fetching, no re-login required.
      set({ admissionLoading: false, admissionLoaded: false });
    }
  },

  /** Admin toggle — persist the flag and update the cached value. */
  setAdmissionModule: async (enabled) => {
    const data = await updateAdmissionSettings({ enable_admission_module: !!enabled });
    set({ enableAdmissionModule: !!data.enable_admission_module, admissionLoaded: true });
    return !!data.enable_admission_module;
  },

  /** Admin — persist the DEPOSIT_LOW threshold (integer paise). */
  setDepositLowThreshold: async (paise) => {
    const data = await updateAdmissionSettings({ deposit_low_threshold_paise: paise });
    const next = Math.max(0, Math.round(Number(data.deposit_low_threshold_paise) || 0));
    set({ depositLowThresholdPaise: next, admissionLoaded: true });
    return next;
  },

  /** Admin — persist bed_day_accrual_enabled and update the cached value. */
  setBedDayAccrual: async (enabled) => {
    const data = await updateAdmissionSettings({ bed_day_accrual_enabled: !!enabled });
    const next = !!data.bed_day_accrual_enabled;
    set({ bedDayAccrualEnabled: next, admissionLoaded: true });
    return next;
  },

  /**
   * Self-heal for a stale tab: the server gate answers 403
   * `{detail:{code:"ADMISSION_DISABLED"}}` on any IPD route once the flag is
   * flipped off elsewhere — force-refetch so the gate redirects. (Interceptor
   * wiring lands with the first gated route in B2; no route emits it yet.)
   */
  onAdmissionDisabled: async () => get().fetchAdmissionSettings({ force: true }),

  // ── Org ABDM HIP ID (abdm_hip_id) ───────────────────────────────────
  // Single source of truth for the org's ABDM Health Information Provider
  // ID — the live routing key for both outbound publish and inbound
  // webhook resolution. Empty string (default) = unset.
  // abdmRegisteredName: the facility name that HIP is registered under with
  // ABDM — the Facility name on every record we publish.
  abdmHipId: '',
  abdmRegisteredName: '',
  // 'ABDM' | 'MANUAL' | '' — an ABDM-sourced name is read-only in the UI.
  abdmNameSource: '',
  // 'VERIFIED' | 'PENDING' | '' plus a reason code (OTHER_BRIDGE, ...) when pending.
  abdmHipStatus: '',
  abdmHipStatusReason: '',
  hipLoaded: false,
  hipLoading: false,

  /** Fetch the per-org HIP ID once unless `force` is set. */
  fetchHipSettings: async ({ force = false } = {}) => {
    const { hipLoaded, hipLoading } = get();
    if ((hipLoaded || hipLoading) && !force) return;
    set({ hipLoading: true });
    try {
      const data = await getHipSettings();
      set({
        abdmHipId: data.abdm_hip_id || '',
        abdmRegisteredName: data.abdm_registered_name || '',
        abdmNameSource: data.abdm_name_source || '',
        abdmHipStatus: data.abdm_hip_status || '',
        abdmHipStatusReason: data.abdm_hip_status_reason || '',
        hipLoaded: true,
        hipLoading: false,
      });
    } catch (err) {
      set({ hipLoading: false, hipLoaded: false });
    }
  },

  /** Admin — persist only the changed fields ({ hipId?, registeredName? }) and cache the result. */
  saveHipSettings: async (changes) => {
    const data = await updateHipSettings(changes);
    set({
      abdmHipId: data.abdm_hip_id || '',
      abdmRegisteredName: data.abdm_registered_name || '',
      abdmNameSource: data.abdm_name_source || '',
      abdmHipStatus: data.abdm_hip_status || '',
      abdmHipStatusReason: data.abdm_hip_status_reason || '',
      hipLoaded: true,
    });
    refreshAbdmHeader();
    return data;
  },

  /** Admin — re-run the ABDM check for the stored HIP ID and cache the result. */
  verifyHipSettings: async () => {
    const data = await verifyHipSettings();
    set({
      abdmHipId: data.abdm_hip_id || '',
      abdmRegisteredName: data.abdm_registered_name || '',
      abdmNameSource: data.abdm_name_source || '',
      abdmHipStatus: data.abdm_hip_status || '',
      abdmHipStatusReason: data.abdm_hip_status_reason || '',
      hipLoaded: true,
    });
    refreshAbdmHeader();
    return data;
  },

  // ── Org ABDM HIU ID (abdm_hiu_id) ───────────────────────────────────
  // Single source of truth for the org's ABDM Health Information User ID —
  // used when this org REQUESTS another facility's records (M3), as
  // distinct from abdmHipId above (used when PUBLISHING this org's own
  // records, M2). Empty string (default) = unset.
  abdmHiuId: '',
  hiuLoaded: false,
  hiuLoading: false,

  /** Fetch the per-org HIU ID once unless `force` is set. */
  fetchHiuSettings: async ({ force = false } = {}) => {
    const { hiuLoaded, hiuLoading } = get();
    if ((hiuLoaded || hiuLoading) && !force) return;
    set({ hiuLoading: true });
    try {
      const data = await getHiuSettings();
      set({ abdmHiuId: data.abdm_hiu_id || '', hiuLoaded: true, hiuLoading: false });
    } catch (err) {
      set({ hiuLoading: false, hiuLoaded: false });
    }
  },

  /** Admin — persist the HIU ID and update the cached value. */
  setHiuId: async (hiuId) => {
    const data = await updateHiuSettings(hiuId);
    set({ abdmHiuId: data.abdm_hiu_id || '', hiuLoaded: true });
    return data.abdm_hiu_id || '';
  },

  // ── Consent catalogue (CONSENT-CAPTURE P1) ─────────────────────────
  // Server-owned text + version + default-checked state — see
  // `## API CONTRACT` in architect-review.md. Fetched once (lazily) and
  // shared across Register Visit, Register Admission and the inline
  // register-patient dialog, exactly like the other org-settings caches
  // above (single-flight via the loaded/isLoading guard).
  consentCatalog: null, // { version, consents: [{ key, label, text, default_granted, sort_order }] }
  consentCatalogLoaded: false,
  consentCatalogLoading: false,

  /** Fetch the platform consent catalogue once unless `force` is set. */
  fetchConsentCatalog: async ({ force = false } = {}) => {
    const { consentCatalogLoaded, consentCatalogLoading } = get();
    if ((consentCatalogLoaded || consentCatalogLoading) && !force) return;
    set({ consentCatalogLoading: true });
    try {
      const data = await getConsentCatalog();
      set({ consentCatalog: data, consentCatalogLoaded: true, consentCatalogLoading: false });
    } catch (err) {
      // Fail open, not closed: leave consentCatalogLoaded false so the next
      // mount retries, and never throw into the caller — a catalogue outage
      // must not make the consent card unusable (D6's default is a fixed
      // server constant that the FE never needs to invent).
      set({ consentCatalogLoading: false, consentCatalogLoaded: false });
    }
  },

}));

export default useOrgSettingsStore;
